import React, { useMemo } from 'react';
import Dropdown, { DropdownProps } from 'atoms/Input/Dropdown';
import Stack from 'atoms/Layout/Stack/Stack';
import MenuWithDropdowns from 'molecules/MenuWithDropdowns/MenuWithDropdowns';
import Grid from 'atoms/Layout/Grid/Grid';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import { convertResourceCategoryToPageSlug } from 'src/functions/resourceCategoryToSlug';

interface ResourceCenterMenuData {
  allContentfulResource: {
    edges: {
      node: {
        subCategory: string;
        category: string;
        slug: string;
        title: string;
      };
    }[];
  };
}

type MenuCategorizedGroup = Array<{ name: string; subCategories: string[] }>;

interface ResourceCategoriesDropdownsProps {
  preSetCategory?: string;
  preSetSubCategory?: string;
}

const ResourceCategoriesDropdowns = ({
  preSetCategory,
  preSetSubCategory,
}: ResourceCategoriesDropdownsProps): JSX.Element => {
  const { allContentfulResource }: ResourceCenterMenuData = useStaticQuery(graphql`
    query ContentfulResourceMenuQuery {
      allContentfulResource(sort: { createdAt: DESC }, filter: { slug: { ne: "attachments-testing" } }) {
        edges {
          node {
            subCategory
            category
            slug
            title
          }
        }
      }
    }
  `);

  const menuCategorizedGroup: MenuCategorizedGroup = useMemo(() => {
    return allContentfulResource.edges.reduce((acc, { node }) => {
      const { category, subCategory } = node;

      if (!category) return acc;

      // Find existing category or create new one
      const existingCategory = acc.find((c) => c.name === category);

      if (existingCategory) {
        // Add subcategory if it doesn't exist
        if (!existingCategory.subCategories.includes(subCategory)) {
          existingCategory.subCategories.push(subCategory);
        }
      } else {
        // Create new category with subcategory
        acc.push({
          name: category,
          subCategories: [subCategory],
        });
      }

      return acc;
    }, [] as MenuCategorizedGroup);
  }, [allContentfulResource]);

  const dropdowns: DropdownProps[] = useMemo(() => {
    return menuCategorizedGroup.map(({ name: category, subCategories }) => ({
      placeholder: category,
      onPlaceholderClick: () => navigate(`/resources/${convertResourceCategoryToPageSlug(category)}`),
      options: subCategories.map((subCategory) => ({
        label: subCategory,
        onClick: () =>
          navigate(
            `/resources/${convertResourceCategoryToPageSlug(category)}/${convertResourceCategoryToPageSlug(
              subCategory
            )}`
          ),
      })),
    }));
  }, [menuCategorizedGroup]);

  const categoryDropdownForMobile = useMemo(() => {
    return {
      label: preSetCategory || 'Filter by Category',
      options: menuCategorizedGroup.map(({ name: category }) => ({
        label: category,
        onClick: () => navigate(`/resources/${convertResourceCategoryToPageSlug(category)}`),
      })),
    };
  }, [menuCategorizedGroup, preSetCategory]);

  const subCategoryDropdownForMobile = useMemo(() => {
    if (!preSetCategory) return null;

    return {
      label: preSetSubCategory || 'Filter by Subcategory',
      options: menuCategorizedGroup
        .find(({ name: category }) => category === preSetCategory)
        ?.subCategories.map((subCategory) => ({
          label: subCategory,
          onClick: () =>
            navigate(
              `/resources/${convertResourceCategoryToPageSlug(preSetCategory)}/${convertResourceCategoryToPageSlug(
                subCategory
              )}`
            ),
        })),
    };
  }, [menuCategorizedGroup, preSetCategory, preSetSubCategory]);

  return (
    <>
      <Stack hideOn='lg'>
        <MenuWithDropdowns dropdowns={dropdowns} />
      </Stack>
      <Grid
        showOn='lg'
        totalColumns={1}
        maxWidth='width-xs'
      >
        <Stack
          gap='spacing-xl'
          direction='column'
          justify='flex-start'
          align='flex-start'
        >
          <Dropdown
            options={categoryDropdownForMobile.options}
            placeholder={categoryDropdownForMobile.label}
            borderRadius='radius-md'
            size='md'
            typographyProps={{
              whiteSpace: 'nowrap',
              type: 'paragraph',
              size: 'sm',
            }}
          />
          {subCategoryDropdownForMobile && (
            <Dropdown
              options={subCategoryDropdownForMobile.options}
              placeholder={subCategoryDropdownForMobile.label}
              borderRadius='radius-md'
              size='md'
              typographyProps={{
                whiteSpace: 'nowrap',
                type: 'paragraph',
                size: 'sm',
              }}
            />
          )}
        </Stack>
      </Grid>
    </>
  );
};

export default ResourceCategoriesDropdowns;
